import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MapPin, UserPlus, UserCheck, Pencil, X, Camera, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Avatar, PostCard } from "@/pages/Archive";

const TABS = [
  { key: "posts", label: "Posts" },
  { key: "photos", label: "Photos" },
  { key: "videos", label: "Videos" },
  { key: "lessons", label: "Lessons" },
  { key: "hunts", label: "Hunts" },
];

function filterPosts(posts, tab) {
  switch (tab) {
    case "photos": return posts.filter((p) => p.media_type !== "video" && ((p.media_list || []).length || p.media));
    case "videos": return posts.filter((p) => p.media_type === "video");
    case "lessons": return posts.filter((p) => p.post_kind === "lesson");
    case "hunts": return posts.filter((p) => ["hunt", "story", "tradition"].includes(p.post_kind));
    default: return posts;
  }
}

export default function ProfilePage() {
  const { id } = useParams();
  const { user, isAuthed } = useAuth();
  const { openAuth } = useUI();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("posts");
  const [editing, setEditing] = useState(false);
  const [busyFollow, setBusyFollow] = useState(false);

  const load = () => api.hunterProfile(id).then(setData).catch(() => setErr(true));
  useEffect(() => { setData(null); setErr(false); load(); }, [id]);

  const follow = async () => {
    if (!isAuthed) return openAuth("register");
    setBusyFollow(true);
    const wasFollowing = data.is_following;
    setData((d) => ({ ...d, is_following: !wasFollowing, followers: d.followers + (wasFollowing ? -1 : 1) }));
    try { await api.toggleFollow(id); }
    catch { setData((d) => ({ ...d, is_following: wasFollowing, followers: d.followers + (wasFollowing ? 1 : -1) })); toast.error("Couldn't update follow."); }
    finally { setBusyFollow(false); }
  };

  if (err) return (
    <div className="bg-paper min-h-screen"><SiteHeader />
      <div className="max-w-3xl mx-auto px-5 py-24 text-center" data-testid="profile-notfound">
        <p className="font-serif text-3xl text-forest">Hunter not found.</p>
        <Link to="/archive" className="text-blaze font-semibold mt-4 inline-block">Back to The Archive</Link>
      </div>
    </div>
  );
  if (!data) return <div className="bg-paper min-h-screen"><SiteHeader /><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div></div>;

  const handle = "@" + (data.display_name || "hunter").toLowerCase().replace(/[^a-z0-9]+/g, "");
  const posts = filterPosts(data.posts || [], tab);
  const bioLine = [data.experience_level, data.primary_species].filter(Boolean).join(" · ");

  return (
    <div className="bg-paper min-h-screen" data-testid="profile-page">
      <SiteHeader />

      {/* Cover */}
      <div className="relative h-48 sm:h-64 bg-charcoal overflow-hidden" data-testid="profile-cover">
        {data.cover_photo
          ? <img src={data.cover_photo} alt="" className="w-full h-full object-cover opacity-80" />
          : <div className="w-full h-full bg-gradient-to-br from-forest via-charcoal to-saddle/60" />}
      </div>

      <div className="max-w-[1100px] mx-auto px-5 sm:px-10">
        {/* Header */}
        <div className="flex items-end gap-5 -mt-12 sm:-mt-14 relative">
          <span className="ring-4 ring-paper rounded-full" data-testid="profile-avatar"><Avatar name={data.display_name} src={data.avatar} size={112} /></span>
          <div className="flex-1 pb-2 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-serif text-3xl sm:text-4xl text-charcoal tracking-tight flex items-center gap-2" data-testid="profile-name">{data.display_name}{data.verified && <BadgeCheck size={22} className="text-forest" data-testid="profile-verified" title="Verified hunter" />}</h1>
              <p className="text-sm text-charcoal/50">{handle}</p>
            </div>
            {data.is_self ? (
              <button onClick={() => setEditing(true)} className="inline-flex items-center gap-2 border border-saddle/30 text-charcoal px-4 py-2 rounded-full text-sm font-semibold hover:border-forest transition-colors" data-testid="profile-edit-btn"><Pencil size={14} /> Edit profile</button>
            ) : (
              <button onClick={follow} disabled={busyFollow} className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${data.is_following ? "border border-saddle/30 text-charcoal hover:border-forest" : "bg-blaze text-white hover:bg-blaze-light"}`} data-testid="profile-follow-btn">
                {data.is_following ? <><UserCheck size={15} /> Following</> : <><UserPlus size={15} /> Follow</>}
              </button>
            )}
          </div>
        </div>

        {/* Bio + stats */}
        <div className="mt-4 space-y-2">
          {data.bio && <p className="text-charcoal/85 max-w-2xl leading-relaxed" data-testid="profile-bio">{data.bio}</p>}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-charcoal/60">
            {data.home_state && <span className="inline-flex items-center gap-1"><MapPin size={14} className="text-blaze" /> {data.home_state}</span>}
            {bioLine && <span>{bioLine}</span>}
          </div>
          <div className="flex items-center gap-5 text-sm pt-1">
            <span data-testid="profile-followers"><strong className="text-charcoal">{data.followers}</strong> <span className="text-charcoal/55">Followers</span></span>
            <span data-testid="profile-following"><strong className="text-charcoal">{data.following}</strong> <span className="text-charcoal/55">Following</span></span>
            <span><strong className="text-charcoal">{(data.posts || []).length}</strong> <span className="text-charcoal/55">Posts</span></span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-6 border-b border-saddle/15 mt-6 overflow-x-auto" data-testid="profile-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`relative py-3 text-sm whitespace-nowrap transition-colors ${tab === t.key ? "text-charcoal font-semibold" : "text-charcoal/55 hover:text-charcoal"}`} data-testid={`profile-tab-${t.key}`}>
              {t.label}
              {tab === t.key && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-blaze" />}
            </button>
          ))}
        </div>

        {/* Feed */}
        <div className="max-w-[620px] mx-auto py-6 space-y-5">
          {posts.length === 0 ? (
            <div className="bg-white border border-dashed border-saddle/30 rounded-md p-12 text-center" data-testid="profile-empty">
              <p className="font-serif text-2xl text-forest">Nothing here yet.</p>
              <p className="text-charcoal/60 mt-2">{data.is_self ? "Share something with The Archive to fill this tab." : `${data.display_name} hasn't posted in ${tab} yet.`}</p>
            </div>
          ) : posts.map((p) => <PostCard key={p.id} post={p} me={user} isAuthed={isAuthed} openAuth={openAuth} onChange={load} />)}
        </div>
      </div>

      {editing && <EditProfileModal data={data} onClose={() => setEditing(false)} onDone={() => { setEditing(false); load(); }} />}
    </div>
  );
}

function EditProfileModal({ data, onClose, onDone }) {
  const [bio, setBio] = useState(data.bio || "");
  const [homeState, setHomeState] = useState(data.home_state || "");
  const [cover, setCover] = useState(data.cover_photo || "");
  const [avatar, setAvatar] = useState(data.avatar || "");
  const [busy, setBusy] = useState(false);

  const readFile = (file, cb) => {
    if (!file || !file.type.startsWith("image/")) return;
    const fr = new FileReader();
    fr.onload = () => { const im = new Image(); im.onload = () => { const c = document.createElement("canvas"); const scale = Math.min(1, 1200 / Math.max(im.width, im.height)); c.width = im.width * scale; c.height = im.height * scale; c.getContext("2d").drawImage(im, 0, 0, c.width, c.height); cb(c.toDataURL("image/jpeg", 0.82)); }; im.src = fr.result; };
    fr.readAsDataURL(file);
  };

  const save = async () => {
    setBusy(true);
    try { await api.updateProfile({ bio: bio.trim() || null, home_state: homeState.trim() || null, cover_photo: cover || null, avatar: avatar || null }); toast.success("Profile updated"); onDone(); }
    catch { toast.error("Couldn't save profile."); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-charcoal/60 grid place-items-center p-4" onClick={onClose} data-testid="edit-profile-modal">
      <div className="bg-paper w-full max-w-md rounded-md max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-saddle/20">
          <h3 className="font-serif text-2xl text-charcoal">Edit profile</h3>
          <button onClick={onClose} aria-label="Close" data-testid="edit-profile-close"><X size={20} className="text-charcoal/50" /></button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-charcoal/50">Cover photo</span>
            <div className="mt-1.5 relative h-24 rounded overflow-hidden border border-saddle/20 bg-charcoal grid place-items-center">
              {cover ? <img src={cover} alt="" className="w-full h-full object-cover" /> : <Camera size={22} className="text-bone/60" />}
              <input type="file" accept="image/*" onChange={(e) => readFile(e.target.files?.[0], setCover)} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="edit-cover-input" />
            </div>
          </label>
          <div className="flex items-center gap-3">
            <span className="relative rounded-full"><Avatar name={data.display_name} src={avatar} size={56} />
              <input type="file" accept="image/*" onChange={(e) => readFile(e.target.files?.[0], setAvatar)} className="absolute inset-0 opacity-0 cursor-pointer rounded-full" data-testid="edit-avatar-input" />
            </span>
            <span className="text-sm text-charcoal/60">Tap to change your avatar</span>
          </div>
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} placeholder="Tell hunters about yourself…" className="w-full bg-white border border-saddle/20 rounded px-3 py-2 text-sm outline-none focus:border-forest resize-none" data-testid="edit-bio" />
          <input value={homeState} onChange={(e) => setHomeState(e.target.value)} placeholder="Home state" className="w-full bg-white border border-saddle/20 rounded px-3 py-2 text-sm outline-none focus:border-forest" data-testid="edit-state" />
        </div>
        <div className="flex items-center justify-end gap-3 p-5 border-t border-saddle/20">
          <button onClick={onClose} className="text-sm font-semibold text-charcoal/60 hover:text-forest">Cancel</button>
          <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-blaze text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors disabled:opacity-60" data-testid="edit-profile-save">
            {busy && <Loader2 size={15} className="animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
